/**
 * Base search form view
 *
 * NOTE: To use this, you must override it with a specific implementation whose job it is to define how the
 * form looks.  That is, override render.  In your render method, call `this.initSubview` to instantiate
 * child controls (see /controls folder).
 *
 * API Methods:
 *     - Reset: Call to empty the search form of all inputs, back to initial state.
 *
 * Available Events:
 *     - 'reset': triggered when the form is reset
 *     - 'active': triggered when the form first becomes active, that is, receives its first search value
 *       after init OR after reset
 *     - 'change': triggered on the change of any child control's value (same as Backbone)
 *     - 'change:attribute': triggered on the change of a child control's value for <property> (same as Backbone)
 */

import Backbone from 'backbone'
import View from 'view/view'

export default View.extend({
    _controls: null,
    _active: false,
    _errors: null,

    initialize() {
        this._controls = {}
        this._errors = {}

        this.model = this.model || new Backbone.Model()

        this.listenTo(this.model, 'all', this._onModelAll.bind(this))
        this.listenTo(this.model, 'change', this._onModelChange.bind(this))
        this.listenTo(this, 'reset', this._onReset)

        this._checkActive()
    },

    bubbleEvents: {
        change: 'onControlChange',
    },

    /**
     * Consumer must implement render.
     * WARNING: Must use `this.initSubview` rather than instantiating the views
     * directly because the parent form must listen to all the child change events
     */
    // eslint-disable-next-line internations/return-this-from-render
    render() {
        throw new Error('Render function is not implemented')
    },

    /**
     * SearchForm API Methods
     * - Reset: Call to reset all form fields
     */

    reset() {
        this.model.clear()
        this.destroySubviews()
        this.render()
        this.trigger('form:reset')
    },

    addControl(control) {
        const name = control.getName()
        if (!name) {
            throw new Error('Control does not have a name')
        }
        this._controls[name] = control
        return control
    },

    getControls() {
        return this._controls
    },

    getControl(name) {
        return this._controls[name]
    },

    getErrors() {
        return this._errors
    },

    setErrors(errors = {}) {
        this._errors = errors
        const controls = this.getControls()
        Object.keys(controls).forEach((name) => {
            const control = controls[name]
            if (control && control.renderError) {
                control.renderError(errors[name])
            }
        })
    },

    /**
     * Listen to all child view change events and update state
     */
    onControlChange(prop, value) {
        this.model.set(prop, value)
        this._checkActive()
    },

    /**
     * Translate all model events onto the view
     */
    _onModelAll(...args) {
        const eventName = args[0]
        args[0] = `form:${eventName}`
        this.trigger(...args)
    },

    _onModelChange() {
        this._checkActive()
    },

    _onReset() {
        this._checkActive()
    },

    _checkActive() {
        // If the user has manually emptied all fields, consider this to be effectively a "reset" event
        // Empty arrays are not considered to be values worthy of active
        const numberOfValues = Object.keys(this.model.attributes).filter((key) => this.model.get(key)).length

        // Was active, now has no search terms
        if (this._active && numberOfValues === 0) {
            this._active = false
            this.reset()
        }

        // Wasn't active, now has search terms
        if (!this._active && numberOfValues > 0) {
            this._active = true
            this.trigger('form:active', this.model)
        }
    },
})
